/*
 * Shared TipTap rich-text editor. Used by:
 *   - CMS Bridge article editor (all features enabled)
 *   - Admin FAQ editor (features prop can hide toolbar items)
 *
 * Design notes:
 *   - HTML is the source of truth. body_html is stored in Mongo; publish
 *     converts to markdown via markdownify on the backend.
 *   - `prose` (from @tailwindcss/typography) is applied to the editor area
 *     so bullets, headings, spacing render as they will on the marketing
 *     site — the editor IS the preview.
 *   - Images upload to Emergent Object Storage on drop/select. The editor
 *     shows the passthrough URL directly (endpoint is public but paths are
 *     unguessable 128-bit UUIDs).
 *   - Video embed auto-detects YouTube, Vimeo, Loom, Instagram Reels;
 *     unknown URLs are inserted as an iframe as-is so anything with an
 *     embeddable URL works (e.g. Wistia, Livid, self-hosted players).
 *   - Icons are lucide-react (universal glyphs, matches Word/Google Docs
 *     conventions) — no cryptic emojis in the toolbar.
 */
import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Subscript as SubIcon, Superscript as SupIcon,
  Heading1, Heading2, Heading3, Pilcrow,
  List, ListOrdered, Quote, Highlighter,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, Video, Table as TableIcon,
  Undo2, Redo2,
} from "lucide-react";

const DEFAULT_FEATURES = {
  headings: true,
  align: true,
  media: true,
  tables: true,
  embeds: true,
  highlight: true,
};

// A toolbar button. Compact, universally-recognisable, keyboard-friendly.
const TBtn = ({ active, onClick, title, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={active ? "true" : "false"}
    data-testid={testId}
    className={`inline-flex items-center justify-center w-8 h-8 rounded border-[1.5px] border-black transition-colors ${
      active ? "bg-[#FFBD4A]" : "bg-white hover:bg-[#FFF7E5]"
    }`}
  >
    {children}
  </button>
);
const Divider = () => <span className="mx-1 self-center h-6 border-l border-black/20" />;

// Video URL → embeddable iframe HTML. Returns null if we can't work it out.
function toEmbedIframe(url) {
  if (!url) return null;
  const u = url.trim();
  let m;
  // YouTube (watch, youtu.be, shorts)
  m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{11})/);
  if (m) {
    return `<div class="aspect-video my-4"><iframe src="https://www.youtube.com/embed/${m[1]}" title="YouTube" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="w-full h-full rounded"></iframe></div>`;
  }
  // Vimeo (vimeo.com/<id> or player.vimeo.com/video/<id>)
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) {
    return `<div class="aspect-video my-4"><iframe src="https://player.vimeo.com/video/${m[1]}" title="Vimeo" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen class="w-full h-full rounded"></iframe></div>`;
  }
  // Loom (share URLs)
  m = u.match(/loom\.com\/(?:share|embed)\/([a-f0-9]+)/i);
  if (m) {
    return `<div class="aspect-video my-4"><iframe src="https://www.loom.com/embed/${m[1]}" title="Loom" frameborder="0" allowfullscreen class="w-full h-full rounded"></iframe></div>`;
  }
  // Instagram Reels / Posts
  m = u.match(/instagram\.com\/(?:reel|p|tv)\/([^\/\?]+)/i);
  if (m) {
    return `<div class="my-4"><iframe src="https://www.instagram.com/p/${m[1]}/embed/" title="Instagram" frameborder="0" scrolling="no" allowtransparency="true" class="w-full h-[540px] rounded"></iframe></div>`;
  }
  // Fallback: if it's an https URL, drop into a generic iframe. Trusts the
  // admin to have pasted an actual embed URL (any provider — Wistia, Livid,
  // self-hosted, etc.).
  if (/^https?:\/\//i.test(u)) {
    return `<div class="aspect-video my-4"><iframe src="${u.replace(/"/g, "&quot;")}" title="Embedded video" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen class="w-full h-full rounded"></iframe></div>`;
  }
  return null;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  features = DEFAULT_FEATURES,
  minHeight = 420,
  testId = "rich-editor",
}) {
  const feat = { ...DEFAULT_FEATURES, ...features };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: feat.headings ? { levels: [1, 2, 3] } : false }),
      Underline,
      Subscript,
      Superscript,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "underline text-black" } }),
      Image.configure({ HTMLAttributes: { class: "max-w-full rounded" } }),
      Highlight.configure({ multicolor: false, HTMLAttributes: { style: "background:#FFBD4A" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: placeholder || "Start writing…" }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        // `prose` gives us WYSIWYG styling for lists, headings, blockquotes,
        // spacing. Scoped classes ensure the marketing site's stylesheet
        // takes over on publish.
        class: "prose prose-sm sm:prose lg:prose-lg max-w-none focus:outline-none px-6 py-5 min-h-[300px] rt-editor-area",
        "data-testid": `${testId}-area`,
      },
    },
  });

  // Sync external value changes when the editor remounts with a loaded article.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && value !== undefined && value !== null) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (leave empty to remove)", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank", rel: "noopener" }).run();
  }, [editor]);

  const uploadImage = useCallback(
    async (file) => {
      if (!editor) return;
      if (!/^image\//.test(file.type)) { toast.error("Only image files supported"); return; }
      if (file.size > 5 * 1024 * 1024) { toast.error("Image over 5MB — please resize"); return; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await axios.post(`${API}/admin/articles/upload-image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Relative URL — routed via ingress to backend, works in preview + prod.
        const url = res.data.preview_url;
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    },
    [editor],
  );

  const pickImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => e.target.files?.[0] && uploadImage(e.target.files[0]);
    input.click();
  }, [uploadImage]);

  const insertVideo = useCallback(() => {
    if (!editor) return;
    const url = window.prompt(
      "Video URL — paste from YouTube, Vimeo, Loom, Instagram Reels, or any provider's embed URL",
    );
    if (!url) return;
    const html = toEmbedIframe(url);
    if (!html) {
      toast.error("Couldn't turn that into an embed. Paste the direct video URL or an embed URL.");
      return;
    }
    editor.chain().focus().insertContent(html).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border-[1.5px] border-black rounded-md bg-white" data-testid={testId}>
      <style>{`
        .rt-editor-area p { margin: 0.75em 0; }
        .rt-editor-area h1 { margin-top: 1.2em; margin-bottom: 0.5em; }
        .rt-editor-area h2 { margin-top: 1em; margin-bottom: 0.4em; }
        .rt-editor-area h3 { margin-top: 0.9em; margin-bottom: 0.3em; }
        .rt-editor-area ul { list-style: disc; padding-left: 1.5em; margin: 0.75em 0; }
        .rt-editor-area ol { list-style: decimal; padding-left: 1.5em; margin: 0.75em 0; }
        .rt-editor-area li { margin: 0.25em 0; }
        .rt-editor-area li > p { margin: 0; }
        .rt-editor-area blockquote { border-left: 3px solid #FFBD4A; padding-left: 1em; margin: 1em 0; font-style: italic; color: #333; }
        .rt-editor-area a { color: #000; text-decoration: underline; }
        .rt-editor-area img { border-radius: 6px; margin: 0.75em 0; max-width: 100%; }
        .rt-editor-area table { border-collapse: collapse; margin: 1em 0; }
        .rt-editor-area th, .rt-editor-area td { border: 1px solid #000; padding: 6px 10px; }
        .rt-editor-area th { background: #FFF7E5; font-weight: 700; }
        .rt-editor-area p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: #999; pointer-events: none; float: left; height: 0;
        }
      `}</style>
      <div className="flex flex-wrap gap-1 p-2 border-b-[1.5px] border-black bg-[#FFF7E5]">
        {/* Text */}
        <TBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)" testId={`${testId}-bold`}><Bold size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)"><Italic size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (Ctrl+U)"><UnderlineIcon size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><Strikethrough size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("subscript")} onClick={() => editor.chain().focus().toggleSubscript().run()} title="Subscript"><SubIcon size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("superscript")} onClick={() => editor.chain().focus().toggleSuperscript().run()} title="Superscript"><SupIcon size={16} strokeWidth={2.5} /></TBtn>
        {feat.highlight && (
          <TBtn active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight"><Highlighter size={16} strokeWidth={2.5} /></TBtn>
        )}
        <Divider />
        {/* Blocks */}
        {feat.headings && (
          <>
            <TBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 size={16} strokeWidth={2.5} /></TBtn>
            <TBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 size={16} strokeWidth={2.5} /></TBtn>
            <TBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 size={16} strokeWidth={2.5} /></TBtn>
          </>
        )}
        <TBtn active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragraph"><Pilcrow size={16} strokeWidth={2.5} /></TBtn>
        <Divider />
        {/* Lists */}
        <TBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote"><Quote size={16} strokeWidth={2.5} /></TBtn>
        {feat.align && (
          <>
            <Divider />
            <TBtn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Align left"><AlignLeft size={16} strokeWidth={2.5} /></TBtn>
            <TBtn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Align centre"><AlignCenter size={16} strokeWidth={2.5} /></TBtn>
            <TBtn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Align right"><AlignRight size={16} strokeWidth={2.5} /></TBtn>
            <TBtn active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()} title="Justify"><AlignJustify size={16} strokeWidth={2.5} /></TBtn>
          </>
        )}
        <Divider />
        {/* Insert */}
        <TBtn active={editor.isActive("link")} onClick={setLink} title="Add link" testId={`${testId}-link`}><LinkIcon size={16} strokeWidth={2.5} /></TBtn>
        {feat.media && (
          <TBtn active={false} onClick={pickImage} title="Insert image" testId={`${testId}-image`}><ImageIcon size={16} strokeWidth={2.5} /></TBtn>
        )}
        {feat.embeds && (
          <TBtn active={false} onClick={insertVideo} title="Embed video (YouTube · Vimeo · Loom · Instagram · other)" testId={`${testId}-video`}><Video size={16} strokeWidth={2.5} /></TBtn>
        )}
        {feat.tables && (
          <TBtn active={false} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon size={16} strokeWidth={2.5} /></TBtn>
        )}
        <Divider />
        {/* Undo / Redo */}
        <TBtn active={false} onClick={() => editor.chain().focus().undo().run()} title="Undo (Ctrl+Z)"><Undo2 size={16} strokeWidth={2.5} /></TBtn>
        <TBtn active={false} onClick={() => editor.chain().focus().redo().run()} title="Redo (Ctrl+Shift+Z)"><Redo2 size={16} strokeWidth={2.5} /></TBtn>
      </div>

      <div style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
